import type {
  CatalogFilterFacet,
  CatalogFilterLockedFacets,
  CatalogFilterPageKind,
  ParsedListingFilters,
} from '../types';

export type CatalogFilterFetchMode = 'ssr' | 'clientReload';

export type CharacterFetchStrategy =
  | 'skip'
  | 'generic'
  | 'fandomEntity'
  | 'universeEntity'
  | 'tagsOnly';

export type FacetFetchStrategy = 'skip' | 'generic' | 'locked';

export interface CatalogFilterFetchPlan {
  fandoms: FacetFetchStrategy;
  tags: FacetFetchStrategy;
  characters: CharacterFetchStrategy;
  universes: FacetFetchStrategy;
  statuses: FacetFetchStrategy;
  ratings: FacetFetchStrategy;
}

const PLAN_CATALOG: CatalogFilterFetchPlan = {
  fandoms: 'generic',
  tags: 'generic',
  characters: 'generic',
  universes: 'generic',
  statuses: 'generic',
  ratings: 'generic',
};

const ALL_CATALOG_FILTER_FACETS: CatalogFilterFacet[] = [
  'fandoms',
  'tags',
  'characters',
  'universes',
  'statuses',
  'ratings',
];

/**
 * Decides which *-custom dictionaries to hit for a catalog page kind.
 *
 * Locked-facet rules:
 * - never send a locked facet into its own dictionary
 * - skip rebuilding fandoms/universes (and tags on tag pages) on client reload
 *   when they are path-identity chrome; `buildFilterOptionsCacheKey` already
 *   prevents a full reload when locks are unchanged
 * - character lists under a fandom use work-level `fandom=` (same join as
 *   fanfics-custom/v2) so `fanfic_cnt` matches the listing — entity `fandomId`
 *   over-counts works that have the character but an empty fandoms array
 * - originals tag permalinks skip fandoms-custom and characters-custom
 *   (those facets are not shown on /originals)
 */
export const buildCatalogFilterFetchPlan = (
  pageKind: CatalogFilterPageKind,
  fetchMode: CatalogFilterFetchMode,
  catalogType: 'fanfic' | 'original' = 'fanfic',
): CatalogFilterFetchPlan => {
  const isClientReload = fetchMode === 'clientReload';
  const isOriginalsCatalog = catalogType === 'original';

  switch (pageKind) {
    case 'fandom':
      return {
        fandoms: isClientReload ? 'skip' : 'generic',
        tags: 'locked',
        characters: 'fandomEntity',
        universes: isClientReload ? 'skip' : 'generic',
        statuses: 'locked',
        ratings: 'locked',
      };
    case 'character':
      return {
        fandoms: isClientReload ? 'skip' : 'generic',
        tags: 'locked',
        characters: 'fandomEntity',
        universes: isClientReload ? 'skip' : 'generic',
        statuses: 'locked',
        ratings: 'locked',
      };
    case 'pairing':
      return {
        fandoms: isClientReload ? 'skip' : 'generic',
        tags: 'locked',
        characters: 'skip',
        universes: isClientReload ? 'skip' : 'generic',
        statuses: 'locked',
        ratings: 'locked',
      };
    case 'tag':
      return {
        fandoms: isOriginalsCatalog ? 'skip' : 'locked',
        tags: isClientReload ? 'skip' : 'generic',
        characters: isOriginalsCatalog ? 'skip' : 'tagsOnly',
        universes: isClientReload ? 'skip' : 'generic',
        statuses: 'locked',
        ratings: 'locked',
      };
    default:
      return PLAN_CATALOG;
  }
};

export const getSkippedFacets = (plan: CatalogFilterFetchPlan): CatalogFilterFacet[] => {
  const skippedFacets: CatalogFilterFacet[] = [];
  if (plan.fandoms === 'skip') skippedFacets.push('fandoms');
  if (plan.tags === 'skip') skippedFacets.push('tags');
  if (plan.characters === 'skip') skippedFacets.push('characters');
  if (plan.universes === 'skip') skippedFacets.push('universes');
  if (plan.statuses === 'skip') skippedFacets.push('statuses');
  if (plan.ratings === 'skip') skippedFacets.push('ratings');
  return skippedFacets;
};

export const getLoadingFacets = (plan: CatalogFilterFetchPlan): CatalogFilterFacet[] => {
  const skippedFacets = new Set(getSkippedFacets(plan));
  return ALL_CATALOG_FILTER_FACETS.filter((facet) => !skippedFacets.has(facet));
};

export const getLockedFacetsFromFilters = (
  pageKind: CatalogFilterPageKind,
  filters: Pick<
    ParsedListingFilters,
    'fandoms' | 'characters' | 'tags' | 'universes' | 'pairingCharacters'
  >,
): CatalogFilterLockedFacets => {
  switch (pageKind) {
    case 'fandom':
      return { fandomId: filters.fandoms[0] };
    case 'character':
      return {
        fandomId: filters.fandoms[0],
        characterIds: filters.characters.slice(0, 1),
      };
    case 'pairing':
      return {
        fandomId: filters.fandoms[0],
        pairingCharacters: filters.pairingCharacters,
      };
    case 'tag':
      return { tagIds: filters.tags.slice(0, 1) };
    default:
      // GET catalog: when a single fandom is selected, scope related facet
      // counts (especially characters) to the same work-level fandom filter
      // the listing uses — otherwise fanfic_cnt stays global and disagrees
      // with the grid (e.g. Narcissa 7 in the filter vs 5 works shown).
      return {
        fandomId: filters.fandoms.length === 1 ? filters.fandoms[0] : undefined,
      };
  }
};

const EMPTY_LISTING_FILTERS: ParsedListingFilters = {
  fandoms: [],
  tags: [],
  universes: [],
  statuses: [],
  ratings: [],
  characters: [],
  pairingCharacters: [],
};

export const buildPermalinkFilterOptionsParams = (
  pageKind: Exclude<CatalogFilterPageKind, 'catalog'>,
  type: 'fanfic' | 'original',
  filters: ParsedListingFilters | null,
) => {
  const safeFilters = filters ?? EMPTY_LISTING_FILTERS;
  return {
    type,
    pageKind,
    fetchMode: 'ssr' as const,
    lockedFacets: getLockedFacetsFromFilters(pageKind, safeFilters),
    selectedCharacterIds: safeFilters.characters,
    selectedTagIds: safeFilters.tags,
  };
};

export const buildFilterOptionsCacheKey = (
  pageKind: CatalogFilterPageKind,
  lockedFacets: CatalogFilterLockedFacets,
): string =>
  JSON.stringify({
    pageKind,
    fandomId: lockedFacets.fandomId ?? '',
    universeId: lockedFacets.universeId ?? '',
    characterIds: lockedFacets.characterIds ?? [],
    pairingCharacters: lockedFacets.pairingCharacters ?? [],
    tagIds: lockedFacets.tagIds ?? [],
  });
